import { useEffect, useState } from "react";
import { toast } from "sonner";
import { House, UploadSimple, Plus, DownloadSimple, ListBullets, Kanban as KanbanIcon, CaretDown, CaretUp, ChatCircleText, Trash, HandPalm, ArrowUUpLeft, CheckCircle, XCircle } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { PendingRepliesPanel } from "../components/leads/PendingRepliesPanel";
import { LeadCommissionTile } from "../components/leads/LeadCommissionTile";
import { api, formatApiErrorDetail } from "../lib/api";
import { useEditConfirm } from "../context/EditConfirmContext";
import { formatET } from "../lib/dateUtils";

const STAGES = ["New", "Contacted", "Qualified", "Proposal", "Won", "Lost"];
const SOURCES = ["trust", "funding", "ecommerce", "other"];
const navItems = [{ to: "/admin/leads", label: "Lead Pipeline", icon: <House size={18} weight="duotone" />, testId: "nav-admin-leads" }];

const SOURCE_TAG_LABELS = {
  datamoon_webhook: "Website Visitor",
  gmail_reply: "Email Reply",
  admin_manual: "Manual",
  client_referral: "Referral",
};
const sourceTagLabel = (createdVia) => SOURCE_TAG_LABELS[createdVia] || (createdVia || "").replaceAll("_", " ");

export default function AdminLeadsPage() {
  const confirmEdit = useEditConfirm();
  const [leads, setLeads] = useState([]);
  const [sourceFilter, setSourceFilter] = useState("all");
  const [stageFilter, setStageFilter] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [newLead, setNewLead] = useState({ name: "", contact: "", source: "other", deal_value: "" });
  const [importing, setImporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importCategory, setImportCategory] = useState("trust");
  const [importFile, setImportFile] = useState(null);
  const [view, setView] = useState("list");
  const [dragLeadId, setDragLeadId] = useState(null);
  const [expandedLeadId, setExpandedLeadId] = useState(null);
  const [reviewOnly, setReviewOnly] = useState(false);
  const [newResponsesOnly, setNewResponsesOnly] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [commissionSummary, setCommissionSummary] = useState(null);
  const [affiliates, setAffiliates] = useState([]);

  const fetchLeads = async () => {
    const params = {};
    if (sourceFilter !== "all") params.source = sourceFilter;
    if (stageFilter !== "all") params.stage = stageFilter;
    if (reviewOnly) params.needs_review = true;
    if (newResponsesOnly) params.intake_qualified = true;
    const { data } = await api.get("/admin/leads", { params });
    setLeads(data);
  };

  const fetchCommissionSummary = async () => {
    const { data } = await api.get("/admin/affiliate-tracker/commission-summary");
    setCommissionSummary(data);
  };

  const fetchAffiliates = async () => {
    const { data } = await api.get("/admin/affiliate-tracker");
    setAffiliates(data.filter((a) => a.status === "active"));
  };

  useEffect(() => {
    fetchCommissionSummary();
    fetchAffiliates();
  }, []);

  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceFilter, stageFilter, reviewOnly, newResponsesOnly]);

  const assignAffiliate = async (leadId, affiliateId) => {
    const l = leads.find((x) => x.id === leadId);
    if (!(await confirmEdit(`${l?.name || "this lead"}'s rep assignment`))) return;
    try {
      await api.patch(`/admin/leads/${leadId}/assign-affiliate`, { affiliate_id: affiliateId || null });
      toast.success("Assignment updated");
      fetchLeads();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const updateStage = async (id, stage) => {
    const l = leads.find((x) => x.id === id);
    if (!(await confirmEdit(`${l?.name || "this lead"}'s stage`))) return;
    try {
      await api.patch(`/admin/leads/${id}`, { stage });
      toast.success("Stage updated");
      fetchLeads();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const addLead = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/leads", { ...newLead, deal_value: parseFloat(newLead.deal_value) || 0 });
      toast.success("Lead added");
      setAddOpen(false);
      setNewLead({ name: "", contact: "", source: "other", deal_value: "" });
      fetchLeads();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    }
  };

  const handleImport = async () => {
    if (!importFile) { toast.error("Choose a CSV file to import"); return; }
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", importFile);
      const { data } = await api.post(`/admin/leads/import?category=${importCategory}`, formData);
      toast.success(data.message);
      setImportOpen(false);
      setImportFile(null);
      fetchLeads();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get("/admin/leads/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "igc_lead_pipeline.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const deleteSelected = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected lead(s)? This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/admin/leads/bulk-delete", { lead_ids: selectedIds });
      toast.success(data.message);
      setSelectedIds([]);
      fetchLeads();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const takeOver = async (id) => {
    try {
      await api.post(`/admin/leads/${id}/take-over`);
      toast.success("You've taken over this conversation");
      fetchLeads();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handBack = async (id) => {
    try {
      await api.post(`/admin/leads/${id}/hand-back`);
      toast.success("Handed back to Nate");
      fetchLeads();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <DashboardLayout navItems={navItems} moduleName="Lead Pipeline" moduleBackPath="/admin">
      <div data-testid="admin-leads-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="font-serif text-3xl text-igc-cream tracking-tight mb-1" title="The master CRM — every lead from every source (website, referrals, manual entry) in one pipeline.">Lead Pipeline</h1>
            <p className="text-igc-muted text-sm">Master CRM — every lead, every division, one place.</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex border border-igc-gold/20">
              <button data-testid="lead-view-list-button" onClick={() => setView("list")} title="Shows leads as a scrollable list of rows." className={`flex items-center gap-1 px-3 py-2 text-xs ${view === "list" ? "bg-igc-gold text-black" : "text-igc-muted"}`}>
                <ListBullets size={14} weight="bold" /> List
              </button>
              <button data-testid="lead-view-kanban-button" onClick={() => setView("kanban")} title="Shows leads as drag-and-drop cards grouped by pipeline stage." className={`flex items-center gap-1 px-3 py-2 text-xs ${view === "kanban" ? "bg-igc-gold text-black" : "text-igc-muted"}`}>
                <KanbanIcon size={14} weight="bold" /> Kanban
              </button>
            </div>
            <Button
              onClick={() => setImportOpen(true)}
              variant="outline"
              data-testid="lead-import-button"
              title="Bulk-adds leads from a CSV file into this pipeline — you'll choose Trust or Funding first."
              className="border-igc-gold text-igc-goldText hover:bg-igc-gold hover:text-black flex items-center gap-2"
            >
              <UploadSimple size={16} weight="duotone" /> Import CSV
            </Button>
            <Button data-testid="lead-export-button" onClick={handleExport} variant="outline" title="Downloads every lead currently shown as a CSV spreadsheet." className="border-igc-gold text-igc-goldText hover:bg-igc-gold hover:text-black flex items-center gap-2">
              <DownloadSimple size={16} weight="duotone" /> Export CSV
            </Button>
            <Button data-testid="lead-add-button" onClick={() => setAddOpen(true)} title="Manually adds one new lead to the pipeline." className="bg-igc-gold hover:bg-igc-goldHover text-black flex items-center gap-2">
              <Plus size={16} weight="bold" /> Add Lead
            </Button>
            {selectedIds.length > 0 && (
              <Button data-testid="lead-bulk-delete-button" onClick={deleteSelected} title="Permanently deletes every lead you've checked below. Cannot be undone." className="bg-igc-danger hover:bg-igc-danger/80 text-white flex items-center gap-2">
                <Trash size={16} weight="bold" /> Delete Selected ({selectedIds.length})
              </Button>
            )}
          </div>
        </div>

        <PendingRepliesPanel onChanged={fetchLeads} />

        <LeadCommissionTile summary={commissionSummary} />

        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger data-testid="lead-source-filter" title="Filter leads by where they came from." className="w-48 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
              <SelectItem value="all">All Sources</SelectItem>
              {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          {view === "list" && (
            <Select value={stageFilter} onValueChange={setStageFilter}>
              <SelectTrigger data-testid="lead-stage-filter" title="Filter leads by their current pipeline stage." className="w-48 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                <SelectItem value="all">All Stages</SelectItem>
                {STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <button
            type="button"
            data-testid="lead-needs-review-filter"
            onClick={() => setReviewOnly(!reviewOnly)}
            title="Shows only leads Nate's AI flagged as needing a human to look at them."
            className={`px-4 py-2 text-sm border transition-colors ${reviewOnly ? "bg-igc-danger/15 border-igc-danger/40 text-igc-danger" : "bg-igc-surfaceLight border-igc-gold/20 text-igc-muted hover:text-igc-cream"}`}
          >
            Needs Manual Review
          </button>
          <button
            type="button"
            data-testid="lead-new-responses-filter"
            onClick={() => setNewResponsesOnly(!newResponsesOnly)}
            className={`px-4 py-2 text-sm border transition-colors ${newResponsesOnly ? "bg-igc-gold/15 border-igc-gold/40 text-igc-goldText" : "bg-igc-surfaceLight border-igc-gold/20 text-igc-muted hover:text-igc-cream"}`}
            title="Leads that answered all 3 qualifying questions from Craigslist / Inbound Email"
          >
            New Lead Responses
          </button>
        </div>

        {view === "list" ? (
          <>
            {leads.length === 0 && <p className="text-igc-muted text-sm" data-testid="lead-empty-state">No leads match this filter.</p>}
            <div className="space-y-2">
              {leads.map((l) => (
                <div key={l.id} className="bg-igc-surface border border-igc-gold/15 p-4" data-testid={`lead-row-${l.id}`}>
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <input
                      type="checkbox"
                      data-testid={`lead-select-checkbox-${l.id}`}
                      checked={selectedIds.includes(l.id)}
                      onChange={() => toggleSelect(l.id)}
                      className="w-4 h-4 accent-[#c9a535]"
                    />
                    <div className="flex-1 min-w-[220px]">
                      <p className="text-igc-cream text-sm flex items-center gap-1.5">
                        {l.client_id && (
                          <span
                            className={`inline-block w-2 h-2 rounded-full ${l.online ? "bg-igc-success" : "bg-igc-muted/40"}`}
                            title={l.online ? "Online now" : l.last_seen_at ? `Last seen ${new Date(l.last_seen_at).toLocaleString()}` : "Never logged in"}
                            data-testid={`lead-presence-dot-${l.id}`}
                          />
                        )}
                        {l.name} {l.client_id && <span className="text-igc-goldText text-xs">(via {l.store_display_name})</span>}
                      </p>
                      <p className="text-igc-muted text-xs">{l.contact} — ${(l.deal_value || 0).toLocaleString()} — {l.source}{typeof l.intent_score === "number" && l.intent_score > 0 ? ` — Intent Score: ${l.intent_score}` : ""}</p>
                      {l.home_address && <p className="text-igc-muted text-xs" data-testid={`lead-address-${l.id}`}>{l.home_address}</p>}
                    </div>
                    <span className="text-[10px] px-2 py-1 tracking-wide bg-igc-gold/10 text-igc-goldText uppercase" data-testid={`lead-source-tag-${l.id}`}>{sourceTagLabel(l.created_via)}</span>
                    {l.needs_manual_review && (
                      <span className="text-[10px] px-2 py-1 tracking-wide bg-igc-danger/15 text-igc-danger uppercase border border-igc-danger/30" data-testid={`lead-review-tag-${l.id}`}>
                        Needs Manual Review
                      </span>
                    )}
                    {l.source === "funding" && l.trulli_client_created === true && (
                      <span className="text-[10px] px-2 py-1 tracking-wide bg-igc-success/10 text-igc-success uppercase border border-igc-success/30 flex items-center gap-1" data-testid={`lead-trulli-synced-${l.id}`}>
                        <CheckCircle size={12} weight="fill" /> Synced to Platform
                      </span>
                    )}
                    {l.source === "funding" && l.trulli_client_created === false && l.trulli_last_attempt && !l.trulli_last_attempt.mocked && (
                      <span className="text-[10px] px-2 py-1 tracking-wide bg-igc-danger/15 text-igc-danger uppercase border border-igc-danger/30 flex items-center gap-1" data-testid={`lead-trulli-sync-failed-${l.id}`}>
                        <XCircle size={12} weight="fill" /> Sync Failed
                      </span>
                    )}
                    {l.conversation?.length > 0 && (
                      <button
                        type="button"
                        data-testid={`lead-conversation-toggle-${l.id}`}
                        onClick={() => setExpandedLeadId(expandedLeadId === l.id ? null : l.id)}
                        className="flex items-center gap-1.5 text-xs text-igc-muted hover:text-igc-goldText transition-colors"
                      >
                        <ChatCircleText size={14} weight="bold" /> {l.conversation.length} message{l.conversation.length === 1 ? "" : "s"}
                        {expandedLeadId === l.id ? <CaretUp size={12} /> : <CaretDown size={12} />}
                      </button>
                    )}
                    {l.nate_paused ? (
                      <Button data-testid={`lead-hand-back-${l.id}`} onClick={() => handBack(l.id)} variant="outline" title="Gives control of this conversation back to Nate's AI auto-responder." className="border-igc-gold/30 text-igc-goldText text-xs h-8 flex items-center gap-1">
                        <ArrowUUpLeft size={12} weight="bold" /> Hand Back to Nate
                      </Button>
                    ) : (
                      <Button data-testid={`lead-take-over-${l.id}`} onClick={() => takeOver(l.id)} variant="outline" title="Pauses Nate's AI auto-responses so you can personally handle this lead's conversation." className="border-igc-gold/30 text-igc-muted text-xs h-8 flex items-center gap-1">
                        <HandPalm size={12} weight="bold" /> Take Over
                      </Button>
                    )}
                    <Select value={l.stage} onValueChange={(v) => updateStage(l.id, v)}>
                      <SelectTrigger data-testid={`lead-stage-select-${l.id}`} title="Where this lead is at in the sales pipeline." className="w-44 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                        {STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={l.assigned_affiliate_id || "none"} onValueChange={(v) => assignAffiliate(l.id, v === "none" ? "" : v)}>
                      <SelectTrigger data-testid={`lead-assign-affiliate-select-${l.id}`} title="Which rep/affiliate this lead is currently assigned to work." className="w-48 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream"><SelectValue placeholder="Assign to affiliate" /></SelectTrigger>
                      <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                        <SelectItem value="none">Unassigned</SelectItem>
                        {affiliates.map((a) => <SelectItem key={a.id} value={a.id}>{a.affiliate_code} — {a.display_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {l.nate_paused && (
                    <p className="text-igc-goldText text-[10px] mt-2 uppercase tracking-wide" data-testid={`lead-nate-paused-tag-${l.id}`}>You've taken over — Nate is not auto-responding to this lead</p>
                  )}
                  {expandedLeadId === l.id && l.conversation?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-igc-gold/10 space-y-3" data-testid={`lead-conversation-thread-${l.id}`}>
                      {l.conversation.map((msg, idx) => (
                        <div key={idx} className={`flex ${msg.direction === "inbound" ? "justify-start" : "justify-end"}`}>
                          <div className={`max-w-[75%] p-3 text-xs ${msg.direction === "inbound" ? "bg-igc-surfaceLight text-igc-cream" : "bg-igc-gold/10 text-igc-cream border border-igc-gold/20"}`}>
                            <p className="text-igc-goldText text-[10px] uppercase tracking-wide mb-1">
                              {msg.direction === "inbound" ? "Lead" : msg.sent_via || "Nate"}
                              {msg.timestamp && <span className="text-igc-muted"> — {formatET(msg.timestamp, "MMM d, h:mm a")} ET</span>}
                              {msg.sent_status && msg.sent_status !== "sent" && <span className="text-igc-danger"> — {msg.sent_status}</span>}
                            </p>
                            <p className="whitespace-pre-wrap">{msg.body}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="grid grid-cols-6 gap-3" data-testid="lead-kanban-board">
            {STAGES.map((stage) => {
              const stageLeads = leads.filter((l) => l.stage === stage);
              return (
                <div
                  key={stage}
                  className="bg-igc-surfaceLight border border-igc-gold/15 p-3 min-h-[300px]"
                  data-testid={`kanban-column-${stage}`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragLeadId) updateStage(dragLeadId, stage);
                    setDragLeadId(null);
                  }}
                >
                  <p className="text-igc-muted text-xs uppercase tracking-widest mb-3">{stage} ({stageLeads.length})</p>
                  <div className="space-y-2">
                    {stageLeads.map((l) => (
                      <div
                        key={l.id}
                        draggable
                        onDragStart={() => setDragLeadId(l.id)}
                        className="bg-igc-surface border border-igc-gold/15 p-3 cursor-move"
                        data-testid={`kanban-card-${l.id}`}
                      >
                        <p className="text-igc-cream text-xs">{l.name}</p>
                        <p className="text-igc-muted text-[10px] mt-1">${(l.deal_value || 0).toLocaleString()} — {l.source}</p>
                        {l.client_id && <p className="text-igc-goldText text-[10px] mt-1">via {l.store_display_name}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent className="bg-igc-surface border-igc-gold/20 text-igc-cream" data-testid="lead-add-modal">
            <DialogHeader><DialogTitle className="font-serif">Add Lead</DialogTitle></DialogHeader>
            <form onSubmit={addLead} className="space-y-3">
              <Input data-testid="lead-add-name-input" required placeholder="Lead name" title="The prospect's full name." value={newLead.name} onChange={(e) => setNewLead({ ...newLead, name: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
              <Input data-testid="lead-add-contact-input" required placeholder="Email or phone" title="An email address or phone number to reach this lead." value={newLead.contact} onChange={(e) => setNewLead({ ...newLead, contact: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
              <Select value={newLead.source} onValueChange={(v) => setNewLead({ ...newLead, source: v })}>
                <SelectTrigger data-testid="lead-add-source-select" className="w-full bg-igc-surfaceLight border-igc-gold/20 text-igc-cream"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                  {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input data-testid="lead-add-value-input" type="number" min="0" placeholder="Estimated deal value ($)" title="Your best guess at how much this deal could be worth if it closes." value={newLead.deal_value} onChange={(e) => setNewLead({ ...newLead, deal_value: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
              <Button data-testid="lead-add-submit-button" type="submit" className="w-full bg-igc-gold hover:bg-igc-goldHover text-black">Add Lead</Button>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={importOpen} onOpenChange={(open) => { setImportOpen(open); if (!open) setImportFile(null); }}>
          <DialogContent className="bg-igc-surface border-igc-gold/20 text-igc-cream" data-testid="lead-import-modal">
            <DialogHeader><DialogTitle className="font-serif">Import CSV</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <p className="text-igc-muted text-xs">Choose which division this batch belongs to — every row is routed as this category regardless of what's in the CSV itself.</p>
              <label className="text-xs uppercase tracking-widest text-igc-muted mb-1 block">Category (required)</label>
              <Select value={importCategory} onValueChange={setImportCategory}>
                <SelectTrigger data-testid="lead-import-category-select" title="Choose whether this CSV contains Trust or Business Funding leads." className="w-full bg-igc-surfaceLight border-igc-gold/20 text-igc-cream"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                  <SelectItem value="trust">Trust & Asset Protection</SelectItem>
                  <SelectItem value="funding">Business Funding</SelectItem>
                </SelectContent>
              </Select>
              <input
                type="file"
                accept=".csv"
                data-testid="lead-import-file-input"
                title="Choose the CSV file of leads to import."
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                className="text-igc-cream text-sm w-full"
              />
              <Button data-testid="lead-import-submit-button" onClick={handleImport} disabled={importing || !importFile} title="Imports this CSV using the category chosen above." className="w-full bg-igc-gold hover:bg-igc-goldHover text-black disabled:opacity-40">
                {importing ? "Importing..." : "Import"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
